import { forwardRef, useId } from 'react';
import clsx from 'clsx';
import { FocusRing } from './FocusRing';
import styles from './Input.module.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      className,
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const hasError = !!error;

    return (
      <div className={clsx(styles.wrapper, className)}>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {props.required && <span className={styles.required}>*</span>}
          </label>
        )}

        <FocusRing>
          <div className={clsx(styles.inputWrapper, hasError && styles.hasError)}>
            {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}

            <input
              ref={ref}
              id={id}
              className={clsx(
                styles.input,
                leftIcon && styles.hasLeftIcon,
                rightIcon && styles.hasRightIcon
              )}
              aria-invalid={hasError}
              aria-describedby={
                [error && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
              }
              {...props}
            />

            {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
          </div>
        </FocusRing>

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className={styles.hint}>
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
